import React, { useEffect, useState } from "react";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    getFirestore,
    limit,
    query,
    where,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { EntityCustomViewParams } from "@firecms/core";
import { AppUser } from "../users";

type DeviceDoc = {
    freeTrialLeft?: number;
};

type FeedbackDoc = {
    id: string;
    title?: string;
    content?: string;
    source?: string;
    appVersion?: string;
    createdAt?: Date;
};

function formatDate(value?: Date): string {
    if (!value) {
        return "–";
    }
    return value.toLocaleString("tr-TR");
}

/**
 * feedbacks koleksiyonunda bu uid var mı — users belgesine alan yazmaz.
 */
export function UserHasFeedbackLabel({ userId }: { userId?: string }) {
    const [label, setLabel] = useState("…");

    useEffect(() => {
        if (!userId) {
            setLabel("Yok");
            return;
        }
        let cancelled = false;
        const db = getFirestore(getApp());
        getDocs(
            query(
                collection(db, "feedbacks"),
                where("userId", "==", userId),
                limit(1),
            ),
        )
            .then((snapshot) => {
                if (!cancelled) {
                    setLabel(snapshot.empty ? "Yok" : "Var");
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setLabel("–");
                }
            });
        return () => {
            cancelled = true;
        };
    }, [userId]);

    return <span>{label}</span>;
}

export function DeviceTrialCount({ deviceId }: { deviceId?: string }) {
    const [label, setLabel] = useState("…");

    useEffect(() => {
        if (!deviceId) {
            setLabel("–");
            return;
        }
        let cancelled = false;
        const db = getFirestore(getApp());
        getDoc(doc(db, "devices", deviceId))
            .then((snapshot) => {
                if (cancelled) {
                    return;
                }
                if (!snapshot.exists()) {
                    setLabel("–");
                    return;
                }
                const data = snapshot.data() as DeviceDoc;
                setLabel(
                    data.freeTrialLeft === undefined || data.freeTrialLeft === null
                        ? "–"
                        : String(data.freeTrialLeft),
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setLabel("–");
                }
            });
        return () => {
            cancelled = true;
        };
    }, [deviceId]);

    return <span>{label}</span>;
}

/**
 * Kullanıcı detayında devices + feedbacks okur; Firestore'a yazmaz.
 */
export function UserRelatedView({ entity }: EntityCustomViewParams<AppUser>) {
    const deviceId = entity?.values.deviceId;
    const userId = entity?.id;
    const [trialLabel, setTrialLabel] = useState("…");
    const [feedbacks, setFeedbacks] = useState<FeedbackDoc[] | null>(null);

    useEffect(() => {
        if (!deviceId) {
            setTrialLabel("–");
            return;
        }
        let cancelled = false;
        const db = getFirestore(getApp());
        getDoc(doc(db, "devices", deviceId))
            .then((snapshot) => {
                if (cancelled) {
                    return;
                }
                if (!snapshot.exists()) {
                    setTrialLabel("–");
                    return;
                }
                const data = snapshot.data() as DeviceDoc;
                setTrialLabel(
                    data.freeTrialLeft === undefined || data.freeTrialLeft === null
                        ? "–"
                        : String(data.freeTrialLeft),
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setTrialLabel("–");
                }
            });
        return () => {
            cancelled = true;
        };
    }, [deviceId]);

    useEffect(() => {
        if (!userId) {
            setFeedbacks([]);
            return;
        }
        let cancelled = false;
        const db = getFirestore(getApp());
        getDocs(query(collection(db, "feedbacks"), where("userId", "==", userId)))
            .then((snapshot) => {
                if (cancelled) {
                    return;
                }
                const items = snapshot.docs.map((item) => {
                    const data = item.data();
                    const createdAt = data.createdAt?.toDate?.() as Date | undefined;
                    return {
                        id: item.id,
                        title: data.title as string | undefined,
                        content: data.content as string | undefined,
                        source: data.source as string | undefined,
                        appVersion: data.appVersion as string | undefined,
                        createdAt,
                    };
                });
                items.sort((a, b) => {
                    const left = a.createdAt?.getTime() ?? 0;
                    const right = b.createdAt?.getTime() ?? 0;
                    return right - left;
                });
                setFeedbacks(items);
            })
            .catch(() => {
                if (!cancelled) {
                    setFeedbacks([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [userId]);

    return (
        <div className="flex flex-col gap-6 p-4">
            <section>
                <h2 className="mb-2 text-lg font-semibold">Cihaz denemesi</h2>
                <p>Cihaz ID: {deviceId || "–"}</p>
                <p>Kalan ücretsiz deneme: {trialLabel}</p>
            </section>
            <section>
                <h2 className="mb-2 text-lg font-semibold">Geri bildirimler</h2>
                {feedbacks === null && <p>Yükleniyor…</p>}
                {feedbacks !== null && feedbacks.length === 0 && <p>Bu kullanıcının geri bildirimi yok.</p>}
                {feedbacks?.map((feedback) => (
                    <article key={feedback.id} className="mb-3 rounded border p-3">
                        <p className="font-medium">{feedback.title || "–"}</p>
                        <p className="whitespace-pre-wrap">{feedback.content || "–"}</p>
                        <p className="text-sm opacity-70">
                            {feedback.source || "–"} · {feedback.appVersion || "–"} · {formatDate(feedback.createdAt)}
                        </p>
                    </article>
                ))}
            </section>
        </div>
    );
}
